import { Button, Input, Modal } from 'antd';
import { type FC, type KeyboardEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  type AtlasGqlGetCommentsQuery,
  type AtlasGqlUpdateCommentInput,
} from 'types/atlas-graphql';
import { type SingularElement } from 'utils/types';

const { TextArea } = Input;

const StyledLinkButton = styled(Button)`
  font-size: 0.75rem !important;
  padding-left: 0 !important;
  height: unset !important;
  color: ${({ theme }) => theme.textColor} !important;
  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    color: ${({ theme }) => theme.textColor} !important;
  }
`;

interface EditCommentProps {
  comment: SingularElement<AtlasGqlGetCommentsQuery['comments']>;
  onEdit: (input: AtlasGqlUpdateCommentInput) => void;
  loading?: boolean;
}

const EditComment: FC<EditCommentProps> = ({ comment: { text, id }, onEdit, loading }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [updatedText, setUpdatedText] = useState<string | null>(null);

  const cancelEdit = () => {
    setOpen(false);
    setUpdatedText(null);
  };

  const editComment = () => {
    onEdit({ id, text: updatedText ?? '' });
    setOpen(false);
    setUpdatedText(null);
  };

  const stopPropagation: KeyboardEventHandler = e => e.stopPropagation();

  return (
    <>
      <StyledLinkButton id="edit-comment-button" type="link" onClick={() => setOpen(true)}>
        <span>{t('common.edit', 'Edit')}</span>
      </StyledLinkButton>
      <Modal
        open={open}
        destroyOnHidden
        title={t('comments.editComment', 'Edit Comment')}
        confirmLoading={loading}
        okText={t('common.saveChanges', 'Save Changes')}
        okButtonProps={{
          id: 'confirm-edit-comment-button',
          disabled: !updatedText,
          type: 'default',
        }}
        onOk={editComment}
        onCancel={cancelEdit}
      >
        <TextArea
          id="edit-comment-input"
          rows={4}
          onChange={e => setUpdatedText(e.target.value)}
          onKeyDown={stopPropagation}
          onKeyUp={stopPropagation}
          defaultValue={text}
        />
      </Modal>
    </>
  );
};

export default EditComment;
